import { useCallback, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { userNeedsEmailPasswordVerification } from '../lib/emailVerification'

/**
 * Blocks the app for email/password users until Firebase marks their email verified (link in inbox).
 * Google sign-in and anonymous users are not affected.
 */
export function EmailVerificationGate({ active }) {
  const { user, sendVerificationEmail, reloadAuthUser, signOut: authSignOut } = useAuth()
  const { t } = useLanguage()
  const [resending, setResending] = useState(false)
  const [checking, setChecking] = useState(false)
  const [banner, setBanner] = useState('')
  const [error, setError] = useState('')

  const needs = Boolean(user && userNeedsEmailPasswordVerification(user))

  const handleResend = useCallback(async () => {
    setError('')
    setBanner('')
    setResending(true)
    try {
      await sendVerificationEmail()
      setBanner(t('auth.verifyEmailResent'))
    } catch (err) {
      const code = err?.code || ''
      if (code === 'auth/too-many-requests') {
        setError(t('auth.verifyEmailTooManyRequests'))
      } else {
        setError(err?.message || t('auth.verifyEmailResendFailed'))
      }
    } finally {
      setResending(false)
    }
  }, [sendVerificationEmail, t])

  const handleContinue = useCallback(async () => {
    setError('')
    setBanner('')
    setChecking(true)
    try {
      const fresh = await reloadAuthUser()
      if (fresh && userNeedsEmailPasswordVerification(fresh)) {
        setError(t('auth.verifyEmailStillPending'))
      }
    } catch (err) {
      setError(err?.message || t('auth.verifyEmailReloadFailed'))
    } finally {
      setChecking(false)
    }
  }, [reloadAuthUser, t])

  if (!active || !needs) return null

  const email = user?.email || ''

  return (
    <div className="email-verify-gate-backdrop" role="dialog" aria-modal="true" aria-labelledby="email-verify-title">
      <div className="email-verify-gate-card">
        <div className="email-verify-gate-header">
          <h2 id="email-verify-title" className="email-verify-gate-title">
            {t('auth.verifyEmailTitle')}
          </h2>
          <p className="email-verify-gate-sub">{t('auth.verifyEmailSubtitle')}</p>
        </div>
        <p className="email-verify-gate-email">
          <strong>{email}</strong>
        </p>
        <ol className="email-verify-gate-steps">
          <li>{t('auth.verifyEmailStep1')}</li>
          <li>{t('auth.verifyEmailStep2')}</li>
          <li>{t('auth.verifyEmailStep3')}</li>
        </ol>
        {banner && <p className="email-verify-gate-banner" role="status">{banner}</p>}
        {error && <p className="email-verify-gate-error" role="alert">{error}</p>}
        <div className="email-verify-gate-actions">
          <button
            type="button"
            className="primary-button email-verify-gate-primary"
            disabled={checking}
            onClick={handleContinue}
          >
            {checking ? t('auth.verifyEmailChecking') : t('auth.verifyEmailContinue')}
          </button>
          <button
            type="button"
            className="ghost-button email-verify-gate-secondary"
            disabled={resending}
            onClick={handleResend}
          >
            {resending ? t('auth.verifyEmailSending') : t('auth.verifyEmailResend')}
          </button>
          <button type="button" className="link-button email-verify-gate-signout" onClick={() => authSignOut()}>
            {t('auth.verifyEmailSignOut')}
          </button>
        </div>
      </div>
    </div>
  )
}
